import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Result } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { ArrowUpRightIcon } from 'app/common/presentation/icons'
import { useConnectionsApi } from 'app/connections/application'
import {
  AvailableConnection,
  AvailableConnectionWithAuthConfig,
  ConnectionAuthConfig,
  ConnectionName,
  isInstalledConnection,
  isOAuthUser,
} from 'app/connections/types'

import { Button, Link } from 'components'

import styles from './AuthorizationButton.module.scss'
import CriteoLogin from './CriteoLogin'
import FacebookLogin from './FacebookLogin'
import GoogleLogin from './GoogleLogin'
import TiktokLogin from './TiktokLogin'
import { getAuthorizationCode } from './getAuthorizationCode'

export type AuthorizationData<AC = string> = {
  authorizationCode: AC
  redirectUri: string
}

export type AuthorizationState = 'OUTDATED' | 'OUTDATED_OR_VALID' | 'PENDING' | 'UNKOWN' | 'VALID'

export interface AuthorizationButtonProps {
  connection: AvailableConnectionWithAuthConfig
  onAuthorizationStateChanged?: (state: AuthorizationState) => void
  onAuthorizationSuccess: (
    authorizationData: AuthorizationData,
  ) => Promise<Result<void, string | void>>
}

const hasOAuthCheck = (connectionName: ConnectionName) =>
  ['facebook', 'googleAdwords', 'googleAnalytics'].includes(connectionName)

export const AuthorizationButton = ({
  connection,
  onAuthorizationStateChanged,
  onAuthorizationSuccess,
}: AuthorizationButtonProps) => {
  const { getOAuth } = useConnectionsApi(connection.name)
  const { createNotification } = useNotifications()
  const { t } = useTranslation('connections')

  const [authorizationState, setAuthorizationState] = useState<AuthorizationState>('UNKOWN')
  const [authorizationError, setAuthorizationError] = useState<string>()

  const authConfig = connection.authConfig
  const authorizationUser =
    isInstalledConnection(connection) && isOAuthUser(connection.authorizationUser)
      ? connection.authorizationUser
      : null
  const redirectUri = `${window.location.origin}/connections/${connection.name.toLowerCase()}`

  useEffect(
    () => onAuthorizationStateChanged?.(authorizationState),
    [authorizationState, onAuthorizationStateChanged],
  )

  useEffect(() => {
    if (isInstalledConnection(connection)) {
      if (hasOAuthCheck(connection.name)) {
        const isOutdated = ['error', 'disabled'].includes(connection.status)
        if (isOutdated) {
          setAuthorizationState('OUTDATED')
        } else {
          void getOAuth().then((result) => {
            if (result.ok) {
              setAuthorizationState('VALID')
            } else {
              setAuthorizationState('OUTDATED')
            }
          })
        }
      } else {
        setAuthorizationState('OUTDATED_OR_VALID')
      }
    } else {
      setAuthorizationState(authorizationUser ? 'VALID' : 'PENDING')
    }
  }, [authorizationUser, connection, getOAuth])

  const handleAuthorizationError = (
    authorizationErrorMessage: string | null,
    withNotification = true,
  ) => {
    const translationKey = `setup.authorization.error.${authorizationErrorMessage}`
    const translatedErrorMessage = t(translationKey)
    const errorMessage =
      translationKey === translatedErrorMessage
        ? `Authorization error: ${authorizationErrorMessage}`
        : translatedErrorMessage

    if (withNotification) {
      createNotification({
        message: errorMessage,
        title: 'Authorization error',
        type: 'error',
      })
    }

    setAuthorizationError(errorMessage)
  }

  const handleAuthorizationSuccess = (authorizationData: AuthorizationData<string | undefined>) =>
    authorizationData.authorizationCode
      ? onAuthorizationSuccess(authorizationData as AuthorizationData).then((result) => {
          if (result.err && result.val) {
            handleAuthorizationError(result.val, false)
          }
        })
      : undefined

  const handleAuthorization = (authConfig: ConnectionAuthConfig, redirectUri: string) =>
    getAuthorizationCode(
      authConfig,
      redirectUri,
      handleAuthorizationSuccess,
      handleAuthorizationError,
      t(`available.${connection.name}.displayName`),
    )

  const requestAuthorization = () => handleAuthorization(authConfig, redirectUri)

  const renderLoginButton = (connection: AvailableConnection) => {
    switch (connection.name) {
      case 'criteo':
        return <CriteoLogin />
      case 'facebook':
        return <FacebookLogin />
      case 'dv360':
      case 'googleAdwords':
      case 'googleAnalytics':
        return <GoogleLogin />
      case 'tiktok':
        return <TiktokLogin />
    }
  }

  const renderAuthorizationState = () => {
    if (!authorizationUser) {
      return (
        <Button rightIcon={<ArrowUpRightIcon />} onClick={requestAuthorization}>
          {t('setup.authorization.oauth.connect', {
            connection: t(`available.${connection.name}.displayName`),
          })}
        </Button>
      )
    }

    switch (authorizationState) {
      case 'OUTDATED':
      case 'OUTDATED_OR_VALID':
        return authorizationUser ? (
          <Button onClick={requestAuthorization} variant="secondary">
            {t('setup.authorization.oauth.reconnect', {
              connection: t(`available.${connection.name}.displayName`),
            })}
          </Button>
        ) : (
          <Link
            title={t(`setup.authorization.${connection.name}.action`)}
            to={requestAuthorization}
          />
        )
      case 'PENDING':
        return <button onClick={requestAuthorization}>{renderLoginButton(connection)}</button>
      case 'VALID':
        return (
          <Button onClick={requestAuthorization} variant="secondary">
            {t('setup.authorization.oauth.reconnect', {
              connection: t(`available.${connection.name}.displayName`),
            })}
          </Button>
        )
    }
  }

  return (
    <div className={styles.signInAuthorization}>
      {renderAuthorizationState()}
      {authorizationError && <div className={styles.signInError}>{authorizationError}</div>}
    </div>
  )
}
